/* eslint-disable no-nested-ternary */
/* eslint-disable max-len */
import React, {
  useEffect, useMemo, useReducer, useRef, useState, forwardRef, useImperativeHandle
} from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import db from '@services/firebase-service';
import { updateWordCloud } from '@services/youtube-platform.service';
import { logToCloudWatch } from '@services/logger.service';
import { useStreamData } from '@lib/hooks/useStreamData';
import { pushDataLayerForEvent } from '@lib/gtag';
import WordChart from './WordChart';
import { handleEngagement } from '@services/interactions-service';
import { chromaPaletteParser } from './helper/chromaPaletteParser';
import { colorPalette } from '@lib/constants';
import useFonts from '@lib/hooks/useFonts';

const Wordcloud = forwardRef(({
  streamId,
  platformType,
  currentRosFeature,
  currentRosResult,
  interactionType,
  setCurrentFeatureId,
  startInteraction,
  splitDiv,
  dispatchTab,
  comments,
  isFullScreen,
  setShowRosModel,
  interactionState,
  moderationModeRef,
  setMessages,
  activeInteractionRef,
  updateFSMeta,
  setShowNotification,
  setStartTime,
  showInstantCustomisationPopup = false,
  setTotalResponders
}, ref) => {
  const {
    firestoreUnsub,
    setFirestoreUnsub,
    setOverrideDismissStatus,
    setFeatureId
  } = useStreamData(s => ({
    firestoreUnsub: s.firestoreUnsub,
    setFirestoreUnsub: s.setFirestoreUnsub,
    setOverrideDismissStatus: s.setOverrideDismissStatus,
    setFeatureId: s.setFeatureId
  }));

  const { isRos, isResult } = interactionType;
  const [wordCloudId, setWordCloudId] = useState('');
  const [wordCloudColors, setWordCloudColors] = useState(currentRosFeature.color);
  const [wordCloudSettings, setwordCloudSettings] = useState(currentRosFeature.setting);

  const [isPaused, _setIsPaused] = useState(false);
  const isPausedRef = useRef(isPaused);
  const setIsPaused = (data) => {
    isPausedRef.current = data;
    _setIsPaused(data);
  };

  const [exclusionList, _setExclusionList] = useState([]);
  const exclusionListRef = useRef(exclusionList);
  const setExclusionList = (data) => {
    exclusionListRef.current = [...exclusionListRef.current, data];
    _setExclusionList([...exclusionList, data]);
  };

  const { getFontStyle } = useFonts();
  const { fontFamily } = getFontStyle(wordCloudSettings?.FontStyle || 'Sans-Serif');

  const [gtagTriggerCounter, setGtagTriggerCounter] = useState(0);

  const newPalette = useMemo(
    () => chromaPaletteParser().parse(colorPalette, wordCloudColors?.wcBgColor),
    [wordCloudColors?.wcBgColor]
  );

  const highchartsRef = useRef(null);

  // Seeds the reducer's initial state on mount; recomputed rather than
  // ref'd since currentRosResult/currentRosFeature may not be settled yet.
  const initialWordCloudData = currentRosResult.length > 0 && currentRosResult[0].comments.length > 0
    ? currentRosResult[0].comments
    : currentRosFeature.comments;

  // Cap rendered words so a long tail of rarely-used words doesn't crowd out
  // the leaders.
  const MAX_RENDERED_WORDS = 40;

  const buildChartOptions = ({
    data, color, colorByPoint, colors, displayStyle, spread
  }) => ({
    // Stable id (the word itself) so WordChart's animation reconciliation
    // tracks a word across updates instead of matching by array index.
    series: [{
      type: 'wordcloud',
      data: (data || [])
        .slice()
        .sort((a, b) => (b.weight || 0) - (a.weight || 0))
        .slice(0, MAX_RENDERED_WORDS)
        .map((point) => ({ ...point, id: point.id || point.name })),
      name: 'Count'
    }],
    chart: { backgroundColor: 'rgba(255, 255, 255, 0.0)', spacing: [0, 0, 0, 0] },
    credits: { enabled: false },
    responsive: {
      rules: [{
        condition: { maxWidth: 500 },
        chartOptions: { legend: { enabled: false } }
      }]
    },
    plotOptions: {
      series: { animation: { duration: 600 } },
      chart: { height: '100%' },
      wordcloud: {
        minFontSize: 12,
        maxFontSize: 150,
        color,
        colorByPoint,
        colors,
        displayStyle: displayStyle || 'chips',
        spread: spread || 'normal',
        style: { fontFamily }
      }
    }
  });

  const options = {
    exporting: { enabled: false },
    title: { text: '' },
    ...buildChartOptions({
      data: initialWordCloudData,
      displayStyle: wordCloudSettings?.wordCloudDisplayStyle,
      spread: wordCloudSettings?.wordCloudSpread,
      color: wordCloudColors?.wcWordColor || '#319795',
      colorByPoint: wordCloudColors?.multipleColors,
      colors: wordCloudColors?.multipleColors ? newPalette : [wordCloudColors?.wcWordColor || '#319795']
    })
  };

  const [chartOptions, dispatch] = useReducer((state, action) => {
    if (action?.data?.length > 0) {
      setGtagTriggerCounter(1);
      if (gtagTriggerCounter === 0) pushDataLayerForEvent('populate_comments_wonder_words');
    }

    if (action.type === 'UPDATE_COLOR') {
      return {
        ...state,
        ...buildChartOptions({
          data: action?.data || state.series[0]?.data || [],
          displayStyle: wordCloudSettings?.wordCloudDisplayStyle,
          spread: wordCloudSettings?.wordCloudSpread,
          color: action?.colors?.wcWordColor || wordCloudColors?.wcWordColor || '#319795',
          colorByPoint: wordCloudColors?.multipleColors,
          colors: wordCloudColors?.multipleColors ? newPalette : [action?.colors?.wcWordColor || wordCloudColors?.wcWordColor || '#319795']
        })
      };
    }

    if (action.type === 'UPDATE_SETTING') {
      return {
        ...state,
        ...buildChartOptions({
          data: action?.data || state.series[0]?.data || [],
          displayStyle: action?.settings?.wordCloudDisplayStyle || wordCloudSettings?.wordCloudDisplayStyle,
          spread: action?.settings?.wordCloudSpread || wordCloudSettings?.wordCloudSpread,
          color: wordCloudColors?.wcWordColor || '#319795',
          colorByPoint: wordCloudColors?.multipleColors,
          colors: wordCloudColors?.multipleColors ? newPalette : [wordCloudColors?.wcWordColor || '#319795']
        })
      };
    }

    return {
      ...state,
      ...buildChartOptions({
        data: action?.data || [],
        displayStyle: wordCloudSettings?.wordCloudDisplayStyle,
        spread: wordCloudSettings?.wordCloudSpread,
        color: wordCloudColors?.wcWordColor || '#319795',
        colorByPoint: wordCloudColors?.multipleColors,
        colors: wordCloudColors?.multipleColors ? newPalette : [wordCloudColors?.wcWordColor || '#319795']
      })
    };
  }, options);

  const setPreviousData = ({ interactionResult = [] }) => {
    if (interactionResult.length > 0 && interactionResult[0].comments.length > 0) {
      dispatch({ type: 'UPDATE_DATA', data: interactionResult[0].comments });
    } else {
      dispatch({ type: 'UPDATE_DATA', data: [] });
    }
  };

  const setupWordCloud = ({ id, settings, colors, noOfResponders, interactionResult }) => {
    dispatch({ data: [] });

    setFeatureId(id);
    setCurrentFeatureId(id);
    setWordCloudId(id);
    setWordCloudColors(colors);
    setwordCloudSettings(settings);

    setPreviousData({ interactionResult });

    setTotalResponders(noOfResponders);
  };

  const handleWordCloud = async ({ interactionId = null, interactionResult = [] }) => {
    const response = await handleEngagement({ streamId, platformType, currentRosFeature, interactionId });

    if (response.status && response.entity) {
      const interactionDetails = {
        id: response.entity.engagementId,
        settings: currentRosFeature.setting,
        colors: currentRosFeature.color,
        noOfResponders: response.entity.noOfResponders || 0,
        interactionResult
      };
      setupWordCloud(interactionDetails);
      updateFSMeta({ activeInteraction: { ...interactionDetails, type: 'wordCloud' } });
    }
  };

  const unsubscribeFromFirestore = () => {
    if (firestoreUnsub.unsub) {
      firestoreUnsub.unsub();
    }
  };

  useEffect(() => {
    if (currentRosFeature.color) {
      setWordCloudColors(currentRosFeature.color);
      dispatch({ type: 'UPDATE_COLOR', colors: currentRosFeature.color, data: currentRosFeature.comments });
    }
  }, [currentRosFeature.color]);

  useEffect(() => {
    if (currentRosFeature.setting) {
      setwordCloudSettings(currentRosFeature.setting);
      dispatch({ type: 'UPDATE_SETTING', settings: currentRosFeature.setting, data: currentRosFeature.comments });
    }
  }, [currentRosFeature.setting]);

  useEffect(() => {
    setPreviousData({ interactionResult: currentRosResult });
  }, [currentRosFeature.id, currentRosResult]);

  useEffect(() => {
    if (wordCloudId && isRos) {
      const logData = {
        streamId,
        interactionType: 'wordcloud',
        interactionId: wordCloudId
      };
      setFirestoreUnsub({
        unsub: onSnapshot(doc(db, 'streams', streamId, 'wordcloud', wordCloudId), (document) => {
          if (document.exists()) {
            if (isPausedRef.current) {
              return;
            }
            const dataList = document.data().data.filter((x) => !exclusionListRef.current.includes(x.name));

            if (dataList.length > 0) setShowNotification(false);

            dispatchTab({ type: 'UPDATE_TAB', data: dataList });

            dispatch({ type: 'UPDATE_DATA', data: dataList });

            setTotalResponders(document.data().noOfResponders || 0);
          }
        }, (error) => logToCloudWatch('Failed to subscribe to firestore', { ...logData, error }, 'ERROR'))
      });
    }

    return () => {
      unsubscribeFromFirestore();
    };
  }, [wordCloudId, isPausedRef.current]);

  const onPauseClick = () => {
    const chart = highchartsRef.current?.chart;
    if (chart?.series[0]) chart.series[0].chart.delImgs = [];
  };

  const deleteComment = async (text) => {
    /**
       We don't want to get the wrong index as data keeps on changing on Moderation Mode.
       Therefore, we pause it while the deletion process is on.
     */
    if (moderationModeRef.current) setIsPaused(true);
    const res = await updateWordCloud({ id: wordCloudId, data: { streamId, word: text?.toLowerCase() } });
    if (res.status) {
      const chart = highchartsRef.current?.chart;
      const cComments = chart?.series[0]?.data.map((comment) => comment.name) || [];
      const index = cComments.indexOf(text);
      if (index > -1) {
        const wordToDelete = chart.series[0].data[index];
        setExclusionList(wordToDelete.name?.toLowerCase());
        wordToDelete.graphic.hide();
      }
    } else {
      setMessages([res.message]);
    }
    if (moderationModeRef.current) setIsPaused(false);
    return res;
  };

  const onPlayClick = () => {
    const chart = highchartsRef.current?.chart;
    chart?.series[0]?.chart?.delImgs?.forEach((img) => {
      img.hide();
    });
    if (chart?.series[0]?.chart?.delImgs) { chart.series[0].chart.delImgs = []; }
  };

  const SendDataToGoogleAnalytics = () => {
    const chart = highchartsRef.current?.chart;
    pushDataLayerForEvent('end_wonder_words_interaction', { no_comments: chart?.series[0]?.data?.length });
  };

  const stopWordCloud = async () => {
    unsubscribeFromFirestore();
    // const sessionId = localStorage.getItem('sessionId');
    // await stopEngagement(streamId, 'wordCloud', { engagementId: wordCloudId, platformType, sessionId });
  };

  useEffect(() => {
    if (activeInteractionRef.current?.id && activeInteractionRef.current?.type === 'wordCloud') {
      setupWordCloud({
        id: activeInteractionRef.current.id,
        colors: activeInteractionRef.current.colors,
        settings: activeInteractionRef.current.settings,
        noOfResponders: activeInteractionRef.current.noOfResponders || 0,
        interactionResult: activeInteractionRef.current.currentRosResult || []
      });
    }
  }, [activeInteractionRef.current]);

  const handleStartInteraction = () => {
    if (currentRosResult && currentRosResult.length > 0) {
      handleWordCloud({ interactionId: currentRosResult[0].id, interactionResult: currentRosResult });
    } else {
      handleWordCloud({ interactionId: null, interactionResult: [] });
    }
    setStartTime(Date.now());
  };

  const handleStopInteraction = () => {
    stopWordCloud();
    setIsPaused(false);
    onPlayClick();
    SendDataToGoogleAnalytics();
    setWordCloudId('');
  };

  const manageWordCloudInteraction = () => {
    if (!isResult && startInteraction) {
      handleStartInteraction();
    } else if (wordCloudId) {
      handleStopInteraction();
    } else if (currentRosResult && currentRosResult.length > 0) {
      setTotalResponders(currentRosResult[0].noOfResponders || 0);
    }
  };

  useEffect(() => {
    if (!currentRosFeature.id) return;

    setShowRosModel(false);
    if (moderationModeRef.current) return;

    manageWordCloudInteraction();

    setOverrideDismissStatus(new Date().getTime());
  }, [currentRosFeature.id, startInteraction]);

  const handleEditBtn = () => {
    if (isPaused) {
      setIsPaused(false); onPlayClick();
    } else {
      setIsPaused(true); onPauseClick();
    }
  };

  useImperativeHandle(ref, () => ({
    handleEditBtn,
    deleteComment,
    async triggerEngagement({ interactionResult }) {
      if (interactionResult && interactionResult.length > 0) {
        await handleWordCloud({ interactionId: interactionResult[0].id, interactionResult });
      }
    }
  }));

  useEffect(() => {
    if (!splitDiv) return;
    window.dispatchEvent(new Event('resize'));
    dispatch({ type: 'UPDATE_DATA', data: comments });
  }, [splitDiv]);

  return (
    <div className={`${showInstantCustomisationPopup ? 'calc(100% - 440px)' : 'w-full'} h-full overflow-hidden rounded-lg flex`}>
      <WordChart
        backgroundColor={'rgba(255, 255, 255, 0)'}
        highchartsRef={highchartsRef}
        chartOptions={chartOptions}
        isFullScreen={isFullScreen}
        splitDiv={splitDiv}
        containerProps={{ style: { width: '100%', height: '100%' } }}
        showInstantCustomisationPopup={showInstantCustomisationPopup}
      />
    </div>
  );
});

export default Wordcloud;
